import { useState } from 'react';

import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { keepPreviousData } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';

import { SectionEnrollment, useGetSectionEnrollments } from 'api/hooks/private/sections/GetSectionEnrollments';

import TimeProgressCell from 'components/DataGrid/CellRenderers/TimeProgressCell';

import { maxDuration } from 'utils/duration';

const columns: GridColDef<SectionEnrollment>[] = [
  {
    field: 'user.last_name',
    type: 'string',
    headerName: 'Last Name',
    minWidth: 150,
    valueGetter: (_value, row) => row.user?.last_name,
  },
  {
    field: 'user.first_name',
    type: 'string',
    headerName: 'First Name',
    minWidth: 150,
    valueGetter: (_value, row) => row.user?.first_name,
  },
  {
    field: 'user.middle_name',
    type: 'string',
    headerName: 'Middle Name',
    minWidth: 150,
    valueGetter: (_value, row) => row.user?.middle_name,
  },
  {
    field: 'enrolled_at',
    type: 'dateTime',
    headerName: 'Date Enrolled',
    minWidth: 200,
    valueGetter: (_, row) => new Date(row.enrolled_at),
    valueFormatter: (_, row) => dayjs(row.enrolled_at).format('MMM Do YYYY h:mm a'),
  },
  {
    field: 'completed_at',
    type: 'dateTime',
    headerName: 'Date Completed',
    minWidth: 200,
    valueGetter: (_, row) => (row.completed_at ? new Date(row.completed_at) : null),
    valueFormatter: (_, row) => (row.completed_at ? dayjs(row.completed_at).format('MMM Do YYYY h:mm a') : null),
  },
  {
    field: 'must_complete_by',
    type: 'dateTime',
    headerName: 'Completion Deadline',
    minWidth: 200,
    valueGetter: (_, row) => (row.must_complete_by ? new Date(row.must_complete_by) : null),
    valueFormatter: (_, row) =>
      row.must_complete_by ? dayjs(row.must_complete_by).format('MMM Do YYYY h:mm a') : null,
  },
  {
    field: 'performance_time_completed',
    headerName: 'Performance Time Completed',
    type: 'string',
    sortable: false,
    minWidth: 250,
    renderCell: (params) => {
      const courseEnrollment = params.row;
      let timeCompleted = courseEnrollment.performance_time_completed;
      const timeRequired = courseEnrollment.course_completion_criteria.performance_time_required;

      if (!timeRequired || dayjs.duration(timeRequired).asMilliseconds() === 0) {
        return 'Not Required';
      }

      // if enrollment has been marked as completed
      if (courseEnrollment.completed_at) {
        // set time completed to the time required
        timeCompleted = timeRequired;
      }

      // return renderTimeCompleted(timeCompleted ?? {}, timeRequired);
      return <TimeProgressCell timeCompleted={timeCompleted} timeRequired={timeRequired} />;
    },
  },
  {
    field: 'performance_time_scheduled',
    headerName: 'Performance Time Scheduled',
    type: 'string',
    sortable: false,
    minWidth: 250,
    renderCell: (params) => {
      const courseEnrollment = params.row;
      let timeCompleted = courseEnrollment.performance_time_completed;
      const timeRequired = courseEnrollment.course_completion_criteria.performance_time_required;

      // if enrollment is for an online course and it has been marked as completed
      if (courseEnrollment.completed_at) {
        // set time completed to the time required
        timeCompleted = {
          hours: maxDuration(timeCompleted, timeRequired).asHours(),
        };
      }

      return <TimeProgressCell timeCompleted={timeCompleted} timeRequired={timeRequired} />;
    },
  },
];

export interface SectionEnrollmentListProps {
  sectionId: string;
}

export function SectionEnrollmentList({ sectionId }: SectionEnrollmentListProps) {
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'user.last_name', sort: 'asc' },
    { field: 'user.first_name', sort: 'asc' },
    { field: 'user.middle_name', sort: 'asc' },
  ]);

  // get section enrollments
  const enrollments = useGetSectionEnrollments({
    variables: {
      sectionId: sectionId,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      sortModel: sortModel.map(({ field, sort }) => ({
        columnName: field,
        sortDir: sort?.toLowerCase() === 'desc' ? 'desc' : 'asc',
      })),
    },
    // use previous data as placeholder to maintain a stable
    // reference while fetching the next page so the datagrid
    // doesn't reset the pagination model
    placeholderData: keepPreviousData,
    enabled: !!sectionId,
  });

  return (
    <DataGrid<SectionEnrollment>
      loading={enrollments.isFetching}
      columns={columns}
      rows={enrollments.data?.rows}
      getRowId={(row) => row.id}
      rowCount={enrollments.data?.count ?? 0}
      onRowClick={(params) => navigate(`/admin/students/${params.row.user.id}`)}
      disableColumnFilter
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={setSortModel}
    />
  );
}

export default SectionEnrollmentList;
